import Phaser from 'phaser'
import { Player } from '../entities/Player'
import { EVENTS, LevelUpEvent } from '../types/GameTypes'

export class StatsSystem {
  private scene: Phaser.Scene
  private player: Player

  // Regen timers
  private hpRegenTimer: number = 0
  private mpRegenTimer: number = 0

  // Regen rates (ms between regen ticks)
  private hpRegenRate: number = 5000  // Regen HP every 5 seconds
  private mpRegenRate: number = 3000  // Regen MP every 3 seconds

  // Regen amounts
  private hpRegenAmount: number = 1
  private mpRegenAmount: number = 2

  constructor(scene: Phaser.Scene, player: Player) {
    this.scene = scene
    this.player = player

    // Listen for level ups to potentially adjust regen
    this.scene.events.on(EVENTS.LEVEL_UP, this.onLevelUp, this)
  }

  /**
   * Update stats (handle regeneration)
   */
  public update(delta: number): void {
    if (this.player.isDead) return

    // HP regeneration (out of combat only - not implemented here for simplicity)
    this.hpRegenTimer += delta
    if (this.hpRegenTimer >= this.hpRegenRate) {
      this.hpRegenTimer = 0
      if (this.player.stats.currentHp < this.player.stats.maxHp) {
        this.player.heal(this.hpRegenAmount)
      }
    }

    // MP regeneration
    this.mpRegenTimer += delta
    if (this.mpRegenTimer >= this.mpRegenRate) {
      this.mpRegenTimer = 0
      if (this.player.stats.currentMp < this.player.stats.maxMp) {
        this.player.restoreMana(this.mpRegenAmount)
      }
    }
  }

  /**
   * Handle level up event
   */
  private onLevelUp(_event: LevelUpEvent): void {
    // Could adjust regen rates based on level
    // For now, just reset regen timers to give immediate benefit
    this.hpRegenTimer = 0
    this.mpRegenTimer = 0
  }

  /**
   * Get HP percentage (0-1)
   */
  public getHpPercent(): number {
    return this.player.stats.currentHp / this.player.stats.maxHp
  }

  /**
   * Get MP percentage (0-1)
   */
  public getMpPercent(): number {
    return this.player.stats.currentMp / this.player.stats.maxMp
  }

  /**
   * Get XP percentage (0-1) toward next level
   */
  public getXpPercent(): number {
    const prevLevelXp = this.player.stats.level > 1
      ? this.player.calculateXPForLevel(this.player.stats.level)
      : 0
    const currentXp = this.player.stats.experience - prevLevelXp
    const xpNeeded = this.player.stats.experienceToNextLevel - prevLevelXp

    return Math.min(1, Math.max(0, currentXp / xpNeeded))
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off(EVENTS.LEVEL_UP, this.onLevelUp, this)
  }
}
